import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import './DefaultAds.css';

const TYPES = ['full', 'bottom', 'top'];
const TYPE_LABELS = { full: 'TV Full Screen', bottom: 'Bottom Bar', top: 'Top Bar' };

const resolvePlaybackMode = (selectedDevice, playbackMode) => {
  if (playbackMode === 'system' || playbackMode === 'suitestream') return playbackMode;
  return selectedDevice?.interfaceMode === 'external_app' || selectedDevice?.externalApp
    ? 'system'
    : 'suitestream';
};

function AdPreview({ url }) {
  const containerRef = useRef(null);
  const iframeRef = useRef(null);

  useLayoutEffect(() => {
    const apply = () => {
      if (!containerRef.current || !iframeRef.current) return;
      const w = containerRef.current.offsetWidth;
      const scale = w / 1920;
      iframeRef.current.style.transform = `scale(${scale})`;
      containerRef.current.style.height = `${Math.round(1080 * scale)}px`;
    };
    apply();
    const ro = new ResizeObserver(apply);
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [url]);

  return (
    <div ref={containerRef} className="ad-preview-container">
      <iframe
        ref={iframeRef}
        src={url}
        title="Ad preview"
        className="ad-preview-iframe"
        scrolling="no"
        tabIndex={-1}
      />
    </div>
  );
}

function DefaultAds({ site, selectedDevice, playbackMode = 'auto' }) {
  const token = localStorage.getItem('token');
  const [overlays, setOverlays] = useState([]);
  const [typeFilter, setTypeFilter] = useState('full');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const siteId = site?.id || null;
  const resolvedPlaybackMode = resolvePlaybackMode(selectedDevice, playbackMode);

  useEffect(() => {
    const fetchDefaultAds = async () => {
      if (!siteId || !token) {
        setOverlays([]);
        return;
      }
      setLoading(true);
      setError('');
      try {
        const res = await axios.get(`/api/default-ads/overlays?siteId=${siteId}&activeOnly=1`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setOverlays(res.data.overlays || []);
      } catch (err) {
        console.error('Failed to load default ads', err);
        setError('Failed to load default ads.');
        setOverlays([]);
      } finally {
        setLoading(false);
      }
    };
    fetchDefaultAds();
  }, [siteId, token]);

  const filtered = useMemo(() => {
    return (overlays || []).filter((o) => String(o.type || '').toLowerCase() === typeFilter);
  }, [overlays, typeFilter]);

  const playNow = async (overlayId) => {
    if (!selectedDevice?.id) {
      alert('Select a device first.');
      return;
    }
    if (!token) {
      alert('You need to sign in again.');
      return;
    }
    try {
      await axios.post(
        '/api/overlays/play',
        { deviceId: selectedDevice.id, overlayId, mode: resolvedPlaybackMode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      console.error('Failed to play overlay', err);
      alert('Failed to play default ad.');
    }
  };

  if (!siteId) {
    return <div className="default-ads-panel">Select a site to view Default Ads.</div>;
  }

  const isFullTab = typeFilter === 'full';

  return (
    <div className="default-ads-panel">
      <div className="default-ads-header">
        <div className="default-ads-header__title">
          <strong>Default Ads</strong>
          <span>Auto-generated from your site details</span>
        </div>
        <div className="chooser-filters" aria-label="Default ad type filter">
          {TYPES.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTypeFilter(t)}
              className={typeFilter === t ? 'active' : ''}
            >
              {TYPE_LABELS[t]}
            </button>
          ))}
        </div>
      </div>

      {loading && <div className="default-ads-loading">Loading…</div>}
      {error && <div className="default-ads-error">{error}</div>}

      {!loading && !filtered.length && (
        <div className="default-ads-empty">
          No active {TYPE_LABELS[typeFilter]} ads.{' '}
          {typeFilter === 'full'
            ? 'Fill in URLs in Settings → Site Details to generate TV ads.'
            : 'Enable URLs in Settings → Site Details.'}
        </div>
      )}

      {isFullTab ? (
        <div className="default-ads-grid">
          {filtered.map((o) => (
            <div key={o.id} className="default-ad-card">
              {o.image_url ? (
                <AdPreview url={o.image_url} />
              ) : (
                <div className="default-ad-card__no-preview">No preview available</div>
              )}
              <div className="default-ad-card__footer">
                <span className="default-ad-card__label">{o.default_ad_label || o.name}</span>
                <button
                  type="button"
                  onClick={() => playNow(o.id)}
                  className="default-ad-play"
                >
                  Play on TV
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="default-ads-list">
          {filtered.map((o) => (
            <div key={o.id} className="default-ad-row">
              <div className="default-ad-main">
                <div className="default-ad-title">{o.default_ad_label || o.name}</div>
                <div className="default-ad-meta">{TYPE_LABELS[o.type] || o.type}</div>
              </div>
              <button
                type="button"
                onClick={() => playNow(o.id)}
                className="default-ad-play"
              >
                Play
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default DefaultAds;
